<template>
  <div>
    <b-row>
      <b-col class="d-flex justify-content-between pb-1 align-items-center">
        <div class="d-flex align-items-center">
          <h1 class="font-weight-bolder mb-0">
            {{ $t('invoices') }}
          </h1>
          <b-badge
            v-if="quoteInvoiceStatusBadge"
            :variant="quoteInvoiceStatusBadge.variant"
            class="ml-1"
          >
            {{ quoteInvoiceStatusBadge.label }}
          </b-badge>
        </div>
        <div class="d-flex align-items-center">
          <b-button
            v-if="canUploadManualInvoice"
            variant="primary"
            class="mr-1"
            @click="openModal"
          >
            {{ $t('uploadInvoice') }}
          </b-button>
          <b-button
            v-if="canIssueQuoteInvoice"
            variant="outline-info"
            class="mr-1"
            :disabled="loading || issuingInvoice || previewingInvoice"
            @click="previewQuoteInvoice"
          >
            <feather-icon
              icon="FileTextIcon"
              class="mr-50"
            />
            {{ previewingInvoice ? 'Generando...' : 'Previsualizar factura' }}
          </b-button>
          <b-button
            v-if="canIssueQuoteInvoice"
            variant="outline-primary"
            :disabled="loading || issuingInvoice || previewingInvoice"
            @click="issueQuoteInvoice"
          >
            {{ issuingInvoice ? 'Facturando...' : 'Facturar cotización' }}
          </b-button>
          <b-button
            v-if="canCancelQuoteInvoice"
            variant="outline-danger"
            class="ml-1"
            :disabled="loading || cancelingInvoice"
            @click="openCancelQuoteInvoiceModal"
          >
            <feather-icon
              icon="XCircleIcon"
              class="mr-50"
            />
            Cancelar factura
          </b-button>
          <b-button
            v-if="canSyncQuoteInvoiceCancellation"
            variant="outline-warning"
            class="ml-1"
            :disabled="loading || syncingInvoiceCancellation"
            @click="syncQuoteInvoiceCancellation"
          >
            <feather-icon
              icon="RefreshCwIcon"
              class="mr-50"
            />
            {{ syncingInvoiceCancellation ? 'Actualizando...' : 'Actualizar cancelación' }}
          </b-button>
          <b-dropdown
            v-if="canDownloadQuoteInvoicesZip"
            variant="outline-success"
            class="ml-1"
            right
            :disabled="loading || downloadingQuoteInvoicesZip"
          >
            <template #button-content>
              <feather-icon
                icon="DownloadIcon"
                class="mr-50"
              />
              Factura y REPs
            </template>
            <b-dropdown-item @click="downloadQuoteInvoicesZip">
              ZIP
            </b-dropdown-item>
          </b-dropdown>
        </div>
      </b-col>
    </b-row>
    <b-alert
      v-if="hasFacturapiRoundingDifference"
      show
      variant="warning"
      class="yc-fiscal-total-alert"
    >
      <strong>Total fiscal diferente a la cotización.</strong>
      Cotización: $ {{ money(quoteCommercialTotal) }} · Factura timbrada: $ {{ money(quoteFiscalTotal) }} · Diferencia:
      {{ signedMoney(quoteFiscalDifference) }}. Para cartera y REP se usa el total fiscal de Facturapi.
    </b-alert>
    <b-row>
      <b-col>
        <b-overlay :show="loading && invoices.length > 0">
          <invoices-table
            :invoices="invoices"
            :loading="loading"
            @refetch="getInvoices"
          />
        </b-overlay>
      </b-col>
    </b-row>
    <b-row>
      <b-col class="d-flex justify-content-end">
        <b-pagination
          :value="quotesPagination.page"
          :total-rows="quotesPagination.total_objects"
          :per-page="quotesPagination.per_page"
          aria-controls="my-table"
        />
      </b-col>
    </b-row>
    <b-modal
      id="newInvoice"
      ok-only
      :ok-title="$t('save')"
      @ok="saveFiles"
    >
      <div class="text-center mb-2">
        <h3>{{ $t('uploadYourInvoice') }}</h3>
      </div>
      <b-row>
        <b-col sm="12">
          <b-form-group>
            <b-form-datepicker
              v-model="newInvoice.date"
              :placeholder="$t('date')"
            />
          </b-form-group>
        </b-col>
        <!-- <b-col
          sm="12"
          lg="6"
        >
          <b-form-group>
            <b-form-input
              v-model="newInvoice.name"
              :placeholder="$t('name')"
              autocomplete="off"
              type="text"
            />
          </b-form-group>
        </b-col>
        <b-col
          sm="12"
          lg="6"
        >
          <b-form-group>
            <b-form-input
              v-model="newInvoice.folio"
              :placeholder="$t('folio')"
              autocomplete="off"
              type="text"
            />
          </b-form-group>
        </b-col> -->
        <b-col
          sm="12"
          class="mt-1"
        >
          <dropzone
            height="150"
            :multiple="true"
          />
        </b-col>
      </b-row>
    </b-modal>
    <b-modal
      id="cancelFacturapiInvoice"
      title="Cancelar factura"
      ok-title="Cancelar factura"
      cancel-title="Cerrar"
      ok-variant="danger"
      :ok-disabled="cancelingInvoice || !canSubmitCancellation"
      @ok.prevent="cancelQuoteInvoice"
      @hidden="resetCancellationForm"
    >
      <b-alert
        show
        variant="warning"
      >
        La cancelación queda registrada con usuario, motivo SAT, razón operativa y respuesta de Facturapi.
      </b-alert>
      <b-form-group label="Motivo SAT">
        <b-form-select
          v-model="cancellationForm.motive"
          :options="cancellationMotiveOptions"
        />
      </b-form-group>
      <b-form-group
        v-if="cancellationForm.motive === '01'"
        label="UUID sustituto"
      >
        <b-form-input
          v-model.trim="cancellationForm.substitution"
          placeholder="UUID del CFDI que sustituye"
        />
      </b-form-group>
      <b-form-group label="Razón operativa">
        <b-form-textarea
          v-model.trim="cancellationForm.reason"
          rows="3"
          max-rows="5"
          placeholder="Describe por qué se cancela la factura"
        />
      </b-form-group>
    </b-modal>
  </div>
</template>

<script>
import { mapGetters, mapActions, mapMutations } from 'vuex'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import InvoicesTable from '@/components/invoice/InvoicesTable.vue'
import Dropzone from '@/components/dropzone/Dropzone.vue'

export default {
  components: {
    InvoicesTable,
    Dropzone,
  },

  data() {
    return {
      loading: false,
      issuingInvoice: false,
      previewingInvoice: false,
      downloadingQuoteInvoicesZip: false,
      cancelingInvoice: false,
      syncingInvoiceCancellation: false,
      invoices: [],
      cancellationForm: {
        motive: '02',
        substitution: '',
        reason: '',
      },
      newInvoice: {
        name: '',
        folio: '',
        file: null,
        date: null,
      },
    }
  },

  computed: {
    ...mapGetters('quotes', ['quotes', 'quotesPagination', 'quote']),
    ...mapGetters('projects', ['project']),
    ...mapGetters('suppliers', {
      files: 'newProviderFiles',
    }),
    ...mapGetters('auth', ['currentUser']),
    isQuoteInvoicesRoute() {
      return this.$route.name === 'quoteInvoices'
    },
    quoteHasFacturapiInvoice() {
      return Boolean(this.quote?.facturapi_invoice_id && this.quote?.facturapi_invoice_status !== 'canceled')
    },
    quoteInvoiceCancellationInProcess() {
      return ['pending', 'verifying', 'in_process'].includes(this.quote?.facturapi_invoice_cancellation_status)
    },
    canCancelQuoteInvoice() {
      return this.isQuoteInvoicesRoute
        && this.currentUser?.role_name === 'admin'
        && this.quoteHasFacturapiInvoice
        && !this.quoteInvoiceCancellationInProcess
    },
    canSyncQuoteInvoiceCancellation() {
      return this.isQuoteInvoicesRoute
        && this.currentUser?.role_name === 'admin'
        && this.quoteHasFacturapiInvoice
        && this.quoteInvoiceCancellationInProcess
    },
    quoteInvoiceStatusBadge() {
      if (!this.isQuoteInvoicesRoute || !this.quote?.facturapi_invoice_id) return null
      return this.$helpers.invoiceStatus(this.quote)
    },
    canDownloadQuoteInvoicesZip() {
      return this.isQuoteInvoicesRoute && this.invoices.length > 0
    },
    quoteCommercialTotal() {
      return Number(this.quote?.selected_total_amount || this.quote?.total || 0)
    },
    quoteFiscalTotal() {
      return Number(this.quote?.facturapi_invoice_total || 0)
    },
    quoteFiscalDifference() {
      return Number((this.quoteFiscalTotal - this.quoteCommercialTotal).toFixed(2))
    },
    hasFacturapiRoundingDifference() {
      return this.quoteHasFacturapiInvoice
        && this.quoteFiscalTotal > 0
        && Math.abs(this.quoteFiscalDifference) >= 0.01
    },
    canIssueQuoteInvoice() {
      return this.isQuoteInvoicesRoute
        && this.currentUser?.role_name === 'admin'
        && !this.quoteHasFacturapiInvoice
    },
    canUploadManualInvoice() {
      if (this.isQuoteInvoicesRoute) {
        return this.currentUser?.role_name === 'admin'
      }

      return this.currentUser?.role_name !== 'customer'
    },
    cancellationMotiveOptions() {
      return [
        { value: '02', text: '02 - Comprobante emitido con errores sin relación' },
        { value: '01', text: '01 - Errores con relación; requiere CFDI sustituto ya emitido' },
        { value: '03', text: '03 - No se llevó a cabo la operación' },
        { value: '04', text: '04 - Operación nominativa relacionada en factura global' },
      ]
    },
    canSubmitCancellation() {
      return Boolean(
        this.cancellationForm.reason
          && this.cancellationForm.motive
          && (this.cancellationForm.motive !== '01' || this.cancellationForm.substitution),
      )
    },
  },

  created() {
    this.loading = true
    if (this.$route.name === 'quoteInvoices') {
      this.fetchQuote(this.$route.params.quoteId)
    }
    if (this.$route.name === 'purchaseOrderInvoices') {
      this.fetchPurchaseOrders(this.$route.params.quoteId)
    }
    this.getInvoices()
  },

  destroyed() {
    this.clearQuotes()
    // this.clearProject({})
  },

  methods: {
    ...mapMutations('quotes', {
      clearQuotes: 'CLEAR_QUOTES',
      changePagination: 'CHANGE_PAGINATION',
    }),
    ...mapMutations('projects', {
      clearProject: 'SET_PROJECT',
    }),
    ...mapActions('quotes', [
      'fetchQuote',
      'getQuoteInovices',
      'createQuoteInovice',
      'downloadQuoteInovices',
    ]),
    ...mapActions('finance', [
      'issueQuoteFacturapiInvoice',
      'previewQuoteFacturapiInvoice',
      'cancelQuoteFacturapiInvoice',
      'syncQuoteFacturapiInvoiceCancellation',
    ]),
    ...mapActions('purchaseOrders', [
      'fetchPurchaseOrders',
      'getPurchaseOrderInovices',
      'createPurchaseOrderInovice',
    ]),
    ...mapActions('projects', ['fetchProject']),

    money(value) {
      const n = Number(value || 0)
      return n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    },
    signedMoney(value) {
      const n = Number(value || 0)
      const sign = n > 0 ? '+' : ''
      return `${sign}$ ${this.money(n)}`
    },
    nextPage(value) {
      this.changePagination({ field: 'page', value })
      this.loading = true
      this.fetchQuotes({
        by_project: this.$route.params.projectId,
        meta: { pagination: this.quotesPagination },
      }).then(() => {
        this.loading = false
      })
    },

    openModal() {
      this.newInvoice = {
        name: '',
        folio: '',
        file: null,
        date: new Date(),
      }
      this.$bvModal.show('newInvoice')
    },
    getInvoices() {
      this.loading = true
      if (this.$route.name === 'quoteInvoices') {
        this.fetchQuote(this.$route.params.quoteId)
          .then(() => this.getQuoteInovices({ quoteId: this.$route.params.quoteId }))
          .then(response => {
            this.invoices = response
          })
          .finally(() => {
            this.loading = false
          })
      } else if (this.$route.name === 'purchaseOrderInvoices') {
        this.getPurchaseOrderInovices({
          purchaseOrderId: this.$route.params.quoteId,
        })
          .then(response => {
            this.invoices = response
          })
          .finally(() => {
            this.loading = false
          })
      }
    },
    issueQuoteInvoice() {
      this.issuingInvoice = true
      this.issueQuoteFacturapiInvoice({
        quoteId: this.$route.params.quoteId,
      }).then(() => {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Factura emitida correctamente',
            icon: 'CheckCircleIcon',
            variant: 'success',
          },
        })
        this.getInvoices()
      }).catch(async error => {
        const messages = await this.previewErrorMessages(error)
        this.$toast({
          component: ToastificationContent,
          props: {
            title: messages[0] || 'No se pudo facturar la cotización',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      }).finally(() => {
        this.issuingInvoice = false
      })
    },
    previewQuoteInvoice() {
      this.previewingInvoice = true
      this.previewQuoteFacturapiInvoice({
        quoteId: this.$route.params.quoteId,
      }).then(response => {
        const blob = new Blob([response.data], { type: response.headers['content-type'] || 'application/pdf' })
        const url = window.URL.createObjectURL(blob)
        window.open(url, '_blank')
        window.setTimeout(() => window.URL.revokeObjectURL(url), 60000)
      }).catch(error => {
        const messages = error?.data?.messages || error?.response?.data?.messages || []
        this.$toast({
          component: ToastificationContent,
          props: {
            title: messages[0] || 'No se pudo generar la previsualización',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      }).finally(() => {
        this.previewingInvoice = false
      })
    },
    downloadQuoteInvoicesZip() {
      this.downloadingQuoteInvoicesZip = true
      this.downloadQuoteInovices({
        id: this.$route.params.quoteId,
        folio: this.quote?.folio || this.$route.params.quoteId,
      }).catch(async error => {
        const messages = await this.previewErrorMessages(error)
        this.$toast({
          component: ToastificationContent,
          props: {
            title: messages[0] || 'No se pudo descargar el ZIP de facturas y REPs',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      }).finally(() => {
        this.downloadingQuoteInvoicesZip = false
      })
    },
    openCancelQuoteInvoiceModal() {
      this.$bvModal.show('cancelFacturapiInvoice')
    },
    resetCancellationForm() {
      this.cancellationForm = {
        motive: '02',
        substitution: '',
        reason: '',
      }
    },
    cancelQuoteInvoice() {
      if (!this.canSubmitCancellation) return

      this.cancelingInvoice = true
      this.cancelQuoteFacturapiInvoice({
        quoteId: this.$route.params.quoteId,
        motive: this.cancellationForm.motive,
        substitution: this.cancellationForm.substitution,
        reason: this.cancellationForm.reason,
      }).then(response => {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: response?.message || 'Factura cancelada correctamente',
            icon: 'CheckCircleIcon',
            variant: 'success',
          },
        })
        this.$bvModal.hide('cancelFacturapiInvoice')
        this.getInvoices()
      }).catch(async error => {
        const messages = await this.previewErrorMessages(error)
        this.$toast({
          component: ToastificationContent,
          props: {
            title: messages[0] || 'No se pudo cancelar la factura',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      }).finally(() => {
        this.cancelingInvoice = false
      })
    },
    syncQuoteInvoiceCancellation() {
      this.syncingInvoiceCancellation = true
      this.syncQuoteFacturapiInvoiceCancellation({
        quoteId: this.$route.params.quoteId,
      }).then(response => {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: response?.message || 'Estado de cancelación actualizado',
            icon: 'RefreshCwIcon',
            variant: response?.cancellation?.state === 'canceled' ? 'success' : 'warning',
          },
        })
        this.getInvoices()
      }).catch(async error => {
        const messages = await this.previewErrorMessages(error)
        this.$toast({
          component: ToastificationContent,
          props: {
            title: messages[0] || 'No se pudo actualizar la cancelación',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      }).finally(() => {
        this.syncingInvoiceCancellation = false
      })
    },
    async previewErrorMessages(error) {
      if (error?.response?.data instanceof Blob) {
        try {
          const text = await error.response.data.text()
          const payload = JSON.parse(text)
          return payload.messages || []
        } catch (e) {
          return []
        }
      }

      return error?.data?.messages || error?.response?.data?.messages || []
    },
    saveFiles() {
      const files = new FormData()
      this.files.forEach(file => {
        files.append('invoice_attachments[]', file)
      })
      files.append('invoiced_at', this.newInvoice.date)
      if (this.$route.name === 'quoteInvoices') {
        this.createQuoteInovice({
          quoteId: this.$route.params.quoteId,
          attachments: files,
        }).then(() => {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: this.$t('filesSaved'),
              icon: 'SaveIcon',
              variant: 'success',
            },
          })
          this.getInvoices()
        })
      } else if (this.$route.name === 'purchaseOrderInvoices') {
        this.createPurchaseOrderInovice({
          purchaseOrderId: this.$route.params.quoteId,
          attachments: files,
        }).then(() => {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: this.$t('filesSaved'),
              icon: 'SaveIcon',
              variant: 'success',
            },
          })
          this.getInvoices()
        })
      }
    },
  },
}
</script>

<style scoped>
.yc-fiscal-total-alert {
  border-color: #f6c46b;
  color: #6d5416;
  font-size: 0.9rem;
}
</style>
